import { useEffect, useRef, useState } from 'react';
import { isRecordingActive, type RecordingStatus } from '@liveforge/shared';
import type { useRecording } from '../../hooks/use-recording';

/**
 * Everything that leaves the studio, in one control in the topbar.
 *
 * It used to be a disc and a word on the programme monitor, which put a
 * session-level decision inside a picture and left nowhere to put the second
 * one. Outputs belong beside Leave: the show is recorded, or streamed, or
 * both, and none of that belongs to the scene being edited.
 *
 * The shape is a **summary and a menu**. The summary is always visible and
 * always says what Egress is actually doing; the commands are one click away,
 * which is right for actions taken twice a show and wrong for a tally that has
 * to be readable at a glance. Streaming lands in the same menu when RTMP is
 * built - the section is already here, and says plainly that it is not.
 */

/** A wall clock, not a date: the show started today or it is not this show. */
const TIME_FORMAT: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit' };

/** `1:02:03` over an hour, `12:34` under one. */
function formatDuration(seconds: number): string {
  const whole = Math.max(0, Math.floor(seconds));
  const parts = [Math.floor(whole / 60) % 60, whole % 60].map((n) => String(n).padStart(2, '0'));
  const hours = Math.floor(whole / 3600);
  return hours > 0 ? `${hours}:${parts.join(':')}` : parts.join(':');
}

/** A file size a person reads, not a byte count they have to parse. */
function formatSize(bytes: number): string {
  if (bytes < 1024 ** 2) return `${Math.round(bytes / 1024)} KB`;
  if (bytes < 1024 ** 3) return `${Math.round(bytes / 1024 ** 2)} MB`;
  return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
}

function secondsBetween(fromIso: string, toIso: string | null): number {
  const to = toIso ? new Date(toIso).getTime() : Date.now();
  return (to - new Date(fromIso).getTime()) / 1000;
}

/**
 * A duration that ticks while the job runs and freezes once it has ended.
 *
 * Its own timer rather than the status poll's: the duration has to move every
 * second, and asking the server that often for a number the browser can work
 * out itself would be absurd. It stops when `until` arrives, so a finished
 * recording says how long it is rather than how long ago it was.
 */
function Duration({ since, until }: { since: string; until: string | null }) {
  const [, tick] = useState(0);

  useEffect(() => {
    if (until) return;
    const timer = window.setInterval(() => tick((n) => n + 1), 1000);
    return () => window.clearInterval(timer);
  }, [until]);

  return <span className="output__clock">{formatDuration(secondsBetween(since, until))}</span>;
}

/**
 * What the studio says about a recording, for every state Egress can be in.
 *
 * `complete` and `failed` used to render as "Record", so a job that died on
 * its own - a disk filling, an instance going away - looked exactly like one
 * nobody had started. Egress has six states and the studio now has six
 * answers.
 *
 * `tone` drives the colour: red only while a file is actually being written,
 * amber for the states that resolve on their own, and for a failure the same
 * red the palette reserves for what cannot be taken back.
 */
function describeRecording(
  status: RecordingStatus,
  unreadable: boolean,
): { label: string; tone: 'idle' | 'live' | 'pending' | 'done' | 'failed' } {
  if (unreadable) return { label: 'Unknown', tone: 'failed' };
  switch (status.state) {
    case 'starting':
      return { label: 'Starting', tone: 'pending' };
    case 'recording':
      return { label: 'Recording', tone: 'live' };
    case 'stopping':
      return { label: 'Finishing', tone: 'pending' };
    case 'complete':
      return { label: 'Recorded', tone: 'done' };
    case 'failed':
      return { label: 'Failed', tone: 'failed' };
    default:
      return { label: 'Not recording', tone: 'idle' };
  }
}

export function OutputBar({ recording }: { recording: ReturnType<typeof useRecording> }) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);

  const { status, busy, error } = recording;
  const active = isRecordingActive(status);
  // A status the studio could not read is not the same as no recording, and
  // the summary has to say which of the two it is. A director told "not
  // recording" by a failed poll is being lied to at the one moment it costs
  // the most.
  const unreadable = error !== null;
  const { label, tone } = describeRecording(status, unreadable);
  /*
   * Stop is offered while `starting`, and that is the whole point.
   *
   * It used to be disabled there, which meant the one state a job can get
   * stuck in - LiveKit accepts the request and no egress instance takes it -
   * was the one state the studio would not let you out of. The backend has
   * always accepted a stop in `starting`; only the button refused.
   */
  const canStop = active;

  // Escape and a click anywhere else, like every other menu a person has used.
  useEffect(() => {
    if (!open) return;
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }
    function onPointerDown(event: MouseEvent) {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    }
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('mousedown', onPointerDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('mousedown', onPointerDown);
    };
  }, [open]);

  return (
    <div className={`output output--${tone}`} ref={root}>
      <button
        type="button"
        className="output__summary"
        onClick={() => setOpen((was) => !was)}
        aria-expanded={open}
        aria-label="Output: recording and streaming"
      >
        <span className="output__dot" aria-hidden="true" />
        <span className="output__state">{label}</span>
        {/*
          From the request, because that is where the file begins - measured,
          not assumed. An earlier version of this held the clock back until
          Egress called itself active, on the reasonable belief that nothing
          was being written before then; the recording says otherwise, and the
          clock has to agree with the artefact rather than with the state.
        */}
        {status.requestedAt && status.state !== 'idle' ? (
          <Duration since={status.requestedAt} until={active ? null : status.endedAt} />
        ) : null}
        <svg
          className="output__chevron"
          viewBox="0 0 24 24"
          width="12"
          height="12"
          aria-hidden="true"
        >
          <path
            d="M6 9l6 6 6-6"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {open ? (
        <div className="output__menu" role="group" aria-label="Output controls">
          <section className="output__section">
            <header className="output__section-head">
              <span className="output__section-title">Recording</span>
              <span className="output__section-state">{label}</span>
            </header>

            {/*
              The numbers a director asks for out loud: when it started, how
              long it has been going, and the handle to quote when something
              has to be looked up in Egress's logs.
            */}
            {status.requestedAt || status.startedAt ? (
              <dl className="output__meta">
                {/*
                  Both clocks, because they are different facts and the gap
                  between them is the one thing a director wants to see while
                  a recording is starting.
                */}
                {status.requestedAt ? (
                  <div className="output__meta-row">
                    <dt>Asked</dt>
                    <dd>{new Date(status.requestedAt).toLocaleTimeString([], TIME_FORMAT)}</dd>
                  </div>
                ) : null}
                <div className="output__meta-row">
                  <dt>Active</dt>
                  <dd className={status.startedAt ? '' : 'output__pending'}>
                    {status.startedAt
                      ? new Date(status.startedAt).toLocaleTimeString([], TIME_FORMAT)
                      : 'not yet'}
                  </dd>
                </div>
                {status.requestedAt ? (
                  <div className="output__meta-row">
                    <dt>{active ? 'Elapsed' : 'Length'}</dt>
                    <dd>
                      <Duration since={status.requestedAt} until={active ? null : status.endedAt} />
                    </dd>
                  </div>
                ) : null}
                {status.sizeBytes ? (
                  <div className="output__meta-row">
                    <dt>Size</dt>
                    <dd>{formatSize(status.sizeBytes)}</dd>
                  </div>
                ) : null}
                {status.egressId ? (
                  <div className="output__meta-row">
                    <dt>Job</dt>
                    <dd className="truncate">{status.egressId}</dd>
                  </div>
                ) : null}
              </dl>
            ) : null}

            {/*
              Egress's own reason, when it has one. It has always been on the
              wire and was never shown, so a recording that failed reported
              nothing at all to the person it failed for.
            */}
            {status.error ? <p className="output__note output__note--bad">{status.error}</p> : null}
            {unreadable ? (
              <p className="output__note output__note--bad">
                The recording state could not be read: {error} What is shown here may be stale.
              </p>
            ) : null}

            <button
              type="button"
              className={`button button--small button--block ${
                canStop ? 'button--danger' : 'button--secondary'
              }`}
              onClick={(event) => {
                if (canStop) {
                  recording.stop();
                  return;
                }
                // Alt starts the same recording with the compositor's
                // diagnostic overlay burnt into it. Deliberately not a control:
                // a director never wants it, and the one person who does knows
                // it is here.
                recording.start(event.altKey);
              }}
              disabled={busy || status.state === 'stopping'}
              title={canStop ? undefined : 'Alt-click to record with the diagnostic overlay'}
            >
              {status.state === 'stopping'
                ? 'Finishing…'
                : status.state === 'starting'
                  ? 'Cancel the start'
                  : canStop
                    ? 'Stop recording'
                    : 'Start recording'}
            </button>

            {/*
              Only when there is a job to be rid of. A recording that never
              started leaves LiveKit in `starting` forever, and every start
              after it is refused as "already being recorded" - this is the way
              out, and it says what it does not do.
            */}
            {status.egressId &&
            (unreadable || status.state === 'starting' || status.state === 'failed') ? (
              <>
                <button
                  type="button"
                  className="button button--small button--block button--ghost"
                  onClick={recording.abandon}
                  disabled={busy}
                >
                  Give up on this job
                </button>
                <p className="output__note">
                  Forgets it so a new recording can be started. It does not promise the media server
                  stopped anything.
                </p>
              </>
            ) : null}

            {status.downloadable ? (
              <>
                <button
                  type="button"
                  className="button button--small button--block button--ghost"
                  onClick={recording.download}
                  disabled={busy}
                >
                  Download the file
                </button>
                {/*
                  The file is named after the Live and nothing else, so a second
                  recording writes over the first. Better said here than
                  discovered afterwards.
                */}
                <p className="output__note">Recording again replaces this file.</p>
              </>
            ) : null}

            {status.state === 'starting' ? (
              <p className="output__note">
                Egress is booting its browser and loading the composition. It is already recording:
                these seconds are in the file, as the composition coming up. The backend gives up on
                the job after 30 seconds rather than leaving it to wait for ever.
              </p>
            ) : active ? (
              <p className="output__note">
                The file can be downloaded once the recording has stopped and Egress has closed it.
              </p>
            ) : null}
          </section>

          <section className="output__section">
            <header className="output__section-head">
              <span className="output__section-title">Stream</span>
              <span className="output__section-state">Off air</span>
            </header>
            <button type="button" className="button button--small button--block" disabled>
              Go live
            </button>
            <p className="output__note">
              RTMP output is not built yet, so nothing can be broadcast from here.
            </p>
          </section>
        </div>
      ) : null}
    </div>
  );
}
